import { CohortHeatmap } from '../../charts/CohortHeatmap'
import { PaceCurve } from '../../charts/PaceCurve'
import {
  AgingStackChart,
  CashPositionChart,
  CoverBarsChart,
  MarginTrendChart,
  ParetoChart,
  PaybackChart,
  RankedBarsChart,
  TargetBulletsChart,
  WeeksBarsChart,
} from '../../charts/cardVisuals'
import type { CeoBasisKind, CeoCardVisual, CeoCockpitResponse } from '../ceoCockpitTypes'

export type CeoCardVisualViewProps = {
  visual: CeoCardVisual
  /** Needed to resolve reference visuals — the pace curve and the cohort grid
   *  point at payload sections that already ship once. */
  payload: CeoCockpitResponse
}

/**
 * The registry: one component per visual kind, dispatched on the server's tag.
 *
 * Self-contained visuals render from their own rows. The two reference kinds
 * (pace curve, cohort grid) resolve against the payload, and resolve to
 * nothing rather than to an empty frame when the referenced section is absent
 * — an empty chart under a real claim reads as evidence withheld.
 */
export function CeoCardVisualView({ visual, payload }: CeoCardVisualViewProps) {
  switch (visual.kind) {
    case 'pace_curve': {
      const curve = payload.pace_curves.find(
        (c) => c.metric_id === visual.metric_id && c.period === visual.period,
      )
      if (!curve || !curve.days.length) return null
      const basis: CeoBasisKind = curve.days.some((d) => d.plan_cum != null)
        ? 'plan'
        : curve.days.some((d) => d.forecast_cum != null)
          ? 'forecast'
          : 'prior_year'
      const lastActual = [...curve.days].reverse().findIndex((d) => !d.is_future)
      const todayIndex = lastActual >= 0 ? curve.days.length - 1 - lastActual : null
      return (
        <PaceCurve
          days={curve.days}
          projection={curve.projection}
          basis={basis}
          todayIndex={todayIndex}
          ariaLabel={visual.caption}
        />
      )
    }
    case 'aging_stack':
      return <AgingStackChart rows={visual.rows} caption={visual.caption} />
    case 'cover_bars':
      return (
        <CoverBarsChart
          rows={visual.rows}
          thresholdDays={visual.threshold_days}
          mode={visual.mode}
          caption={visual.caption}
        />
      )
    case 'target_bullets':
      return <TargetBulletsChart rows={visual.rows} unit={visual.unit} caption={visual.caption} />
    case 'weeks_bars': {
      // The payload names its unit; older snapshots predate the field, so
      // fall back to the ratio-vs-dollars inference they were built under.
      const unit = visual.unit ?? (visual.weeks.every((w) => Math.abs(w.value) < 5) ? 'pct' : 'usd')
      return <WeeksBarsChart weeks={visual.weeks} median={visual.median} unit={unit} caption={visual.caption} />
    }
    case 'margin_trend':
      return (
        <MarginTrendChart
          months={visual.months}
          usual={visual.usual}
          highlightPeriod={visual.highlight_period}
          caption={visual.caption}
        />
      )
    case 'pareto':
      return <ParetoChart rows={visual.rows} caption={visual.caption} />
    case 'payback':
      return (
        <PaybackChart
          months={visual.months}
          comfortMonths={visual.comfort_months}
          cac={visual.cac}
          ltvMultiple={visual.ltv_multiple}
          caption={visual.caption}
        />
      )
    case 'cash_position':
      return (
        <CashPositionChart
          now={visual.now}
          floor={visual.floor}
          low13w={visual.low_13w}
          weeksAhead={visual.weeks_ahead}
          caption={visual.caption}
        />
      )
    case 'ranked_bars':
      return <RankedBarsChart rows={visual.rows} unit={visual.unit} caption={visual.caption} />
    case 'cohort_grid': {
      const cohorts = payload.saas?.cohorts
      if (!cohorts || !cohorts.rows?.length) return null
      return <CohortHeatmap ages={cohorts.ages} rows={cohorts.rows} ariaLabel={visual.caption} />
    }
    default:
      return null
  }
}
